export const ONBOARDING_DEMO_SEARCH_NAME = "Demo Search";
export const ONBOARDING_DEMO_SEARCH_DESCRIPTION = "Demonstrative Search";
export const ONBOARDING_DEMO_SEARCH_CADENCE = "WEEKLY" as const;

type SearchSpecificationListItem = {
  id: string;
  createdAt: Date | string;
};

type SearchSpecificationPage = {
  items: SearchSpecificationListItem[];
  totalCount: number;
};

export interface ResolveOnboardingSearchSpecificationInput {
  brandId: string;
  personaId: string;
  demoLlmIds: string[];
  fetchBrandSearchSpecifications: (input: { brandId: string; skip: number; take: number }) => Promise<SearchSpecificationPage>;
  createSearchSpecification: (input: {
    brandId: string;
    name: string;
    description: string;
    llmIds: string[];
    personaId: string;
  }) => Promise<{ id: string }>;
  updateSearchSpecification: (input: {
    id: string;
    cadence: typeof ONBOARDING_DEMO_SEARCH_CADENCE;
    personaId?: string;
    llmIds?: string[];
  }) => Promise<unknown>;
}

const sortByCreatedAtAscending = (items: SearchSpecificationListItem[]) =>
  [...items].sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
  );

export const resolveOnboardingSearchSpecification = async (
  input: ResolveOnboardingSearchSpecificationInput,
): Promise<string> => {
  const allSearchSpecifications: SearchSpecificationListItem[] = [];
  const take = 100;
  let skip = 0;
  let totalCount = 0;

  do {
    const page = await input.fetchBrandSearchSpecifications({
      brandId: input.brandId,
      skip,
      take,
    });
    totalCount = page.totalCount;
    allSearchSpecifications.push(...page.items);
    skip += take;
  } while (allSearchSpecifications.length < totalCount);

  if (!allSearchSpecifications.length) {
    const created = await input.createSearchSpecification({
      brandId: input.brandId,
      name: ONBOARDING_DEMO_SEARCH_NAME,
      description: ONBOARDING_DEMO_SEARCH_DESCRIPTION,
      llmIds: input.demoLlmIds,
      personaId: input.personaId,
    });

    await input.updateSearchSpecification({
      id: created.id,
      cadence: ONBOARDING_DEMO_SEARCH_CADENCE,
    });

    return created.id;
  }

  const firstSearchSpecification = sortByCreatedAtAscending(allSearchSpecifications)[0];
  if (!firstSearchSpecification) {
    throw new Error("FIRST_SEARCH_SPECIFICATION_NOT_FOUND");
  }

  await input.updateSearchSpecification({
    id: firstSearchSpecification.id,
    cadence: ONBOARDING_DEMO_SEARCH_CADENCE,
    personaId: input.personaId,
    ...(input.demoLlmIds.length > 0 ? { llmIds: input.demoLlmIds } : {}),
  });

  return firstSearchSpecification.id;
};
